import { utils, write } from 'xlsx';
import type { WorkSheet } from 'xlsx';

/** Excel number format so values in the 1–999 range are not shown as dates when the sheet is opened in regional Excel. */
const MONETARY_EXCEL_NUMFMT = '#,##0.00';

export interface SaveXlsxOptions {
  /**
   * Header labels of columns (first row) that contain monetary amounts.
   * Those cells get an explicit number format so Excel does not treat values like 102.25 as date serials.
   */
  monetaryColumnHeaders?: string[];
}

function applyMonetaryNumberFormat(sheet: WorkSheet, monetaryHeaders: string[]): void {
  if (!sheet['!ref'] || !monetaryHeaders.length) {
    return;
  }
  const range = utils.decode_range(sheet['!ref']);
  const headerSet = new Set(monetaryHeaders);
  const headerRow = range.s.r;

  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerAddr = utils.encode_cell({ r: headerRow, c });
    const headerCell = sheet[headerAddr];
    const headerText =
      headerCell && headerCell.t === 's' && typeof headerCell.v === 'string' ? headerCell.v : '';
    if (!headerSet.has(headerText)) {
      continue;
    }
    for (let r = headerRow + 1; r <= range.e.r; r++) {
      const addr = utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (cell && cell.t === 'n' && typeof cell.v === 'number' && Number.isFinite(cell.v)) {
        cell.z = MONETARY_EXCEL_NUMFMT;
      }
    }
  }
}

/** Excel worksheet names are max 31 chars; \ / * ? : [ ] are not allowed. */
const excelSheetName = (fileName: string): string => {
  let base = fileName.replace(/\.xlsx$/i, '').trim();
  for (const ch of ['\\', '/', '*', '?', ':', '[', ']'] as const) {
    base = base.split(ch).join(' ');
  }
  base = base.replace(/\s+/g, ' ').trim();
  const truncated = base.length > 31 ? base.slice(0, 31) : base;
  return truncated || 'Sheet1';
};

export const saveXLSX = (
  data: Record<string, any>[],
  name: string = 'sheet',
  options?: SaveXlsxOptions
): void => {
  const sheet = utils.json_to_sheet(data);
  if (options?.monetaryColumnHeaders?.length) {
    applyMonetaryNumberFormat(sheet, options.monetaryColumnHeaders);
  }
  const book = utils.book_new();
  utils.book_append_sheet(book, sheet, excelSheetName(name));

  const excelBuffer = write(book, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}`;
  a.click();
  URL.revokeObjectURL(url);
};
